import React from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridColumnGroup,
  GridColumnGroupHeaderParams,
  GridColumnGroupingModel,
  GridRowId,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import DownloadIcon from '@mui/icons-material/Download';
import HeaderText from "../baseComponents/gridBoldHeader";
import HeaderButton from "../baseComponents/headerButton";
import { GridColumnVisibilityModel } from "@mui/x-data-grid";

interface SearchResultGridWithActionsProps {
  results: any[];
  getRowId: (row: any) => GridRowId;
  onSelectRow: (id: GridRowSelectionModel) => void;
  columns: GridColDef[];
  height?: number | string;
  rowHeight?: number;
  sortModel?: any[];
  disableFilter?: boolean;
  disableSorting?: boolean;
  disableAsecDescSorting?: boolean;
  rowCount?: number;
  paginationModel?: { page: number; pageSize: number };
  setPaginationModel?: React.Dispatch<
    React.SetStateAction<{ page: number; pageSize: number }>
  >;
  isRowSelectionAllowed?: boolean;
  columnGroupModel?: GridColumnGroupingModel;
  showToolBar?: boolean;
  pageSizeOption?: number[];
  groupVisibilityMap?: Record<string, { visible: boolean; button: boolean; }>;
  toggleGroupVisibility?: (groupHeaderName: string) => void;
  rowSelectionModel?: GridRowSelectionModel;
  hideFooter?: boolean;
  disableMultipleRowSelection?: boolean;
  columnVisibilityModel?: GridColumnVisibilityModel;
  includeHiddenColumnInExport?: boolean;
  disableDownloadByPrintOptions?: boolean;
  disableDownloadAsCSVOptions?: boolean;
  disableColumnSelector?: boolean;
  onDownloadFullFile?: () => void;
  downloadFullFileButtonText?: string;
  isDownloadingFullFile?: boolean;
}

const SearchResultGrid: React.FC<SearchResultGridWithActionsProps> = ({
  getRowId,
  results,
  onSelectRow,
  columns,
  height,
  rowHeight = 32,
  sortModel,
  disableFilter,
  disableSorting,
  disableAsecDescSorting,
  rowCount,
  paginationModel,
  setPaginationModel,
  isRowSelectionAllowed,
  pageSizeOption,
  columnGroupModel,
  showToolBar,
  groupVisibilityMap,
  toggleGroupVisibility,
  rowSelectionModel,
  hideFooter,
  disableMultipleRowSelection,
  columnVisibilityModel,
  includeHiddenColumnInExport = false,
  disableDownloadByPrintOptions = false,
  disableDownloadAsCSVOptions = false,
  disableColumnSelector = false,
  onDownloadFullFile,
  downloadFullFileButtonText = "Export",
  isDownloadingFullFile = false,
}) => {
  
  // Custom toolbar when onDownloadFullFile is provided
  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        {!disableColumnSelector && <GridToolbarColumnsButton />}
        {!disableFilter && <GridToolbarFilterButton />}
        <Button
          size="small"
          startIcon={isDownloadingFullFile ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={onDownloadFullFile}
          disabled={isDownloadingFullFile}
        >
          {isDownloadingFullFile ? 'Downloading...' : downloadFullFileButtonText}
        </Button>
      </GridToolbarContainer>
    );
  };

  return (
    <Box
      sx={{ height: height ? height : "65vh", width: "100%", fontSize: "12px" }}
    >
      <DataGrid
        showToolbar={showToolBar === undefined ? true : showToolBar}
        rowHeight={rowHeight}
        columnHeaderHeight={36}
        getRowId={getRowId}
        rows={results}
        columns={columns.map((val) => {
          val.renderHeader = HeaderText;
          return val;
        })}
        checkboxSelection={
          isRowSelectionAllowed === true || isRowSelectionAllowed === false
            ? isRowSelectionAllowed
            : true
        }
        rowCount={rowCount ? rowCount : results.length}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationMode={hideFooter ? undefined : "server"}
        hideFooter={hideFooter}
        sortModel={sortModel}
        disableMultipleRowSelection={disableMultipleRowSelection}
        columnGroupingModel={columnGroupModel?.map((model: GridColumnGroup) => {
          model.renderHeaderGroup = (params: GridColumnGroupHeaderParams) => (
            <HeaderButton
              groupHeaderName={params.groupId || ""}
              isGroupVisible={
                (groupVisibilityMap || {})[params.groupId || ""].visible
              }
              toggleGroupVisibility={(groupHeaderName) => {
                if (toggleGroupVisibility) {
                  toggleGroupVisibility(groupHeaderName);
                }
              }}
              noButton={
                !(groupVisibilityMap || {})[params.groupId || ""].button
              }
            />
          );
          return model;
        })}
        disableColumnFilter={disableFilter ? true : false}
        disableColumnSorting={disableSorting ? true : false}
        sortingOrder={!disableAsecDescSorting ? ["asc", "desc"] : undefined}
        pageSizeOptions={pageSizeOption ? pageSizeOption : [40]}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(newSelection) => {
          onSelectRow(newSelection);
        }}
        columnVisibilityModel={columnVisibilityModel}
        slots={onDownloadFullFile ? { toolbar: CustomToolbar } : undefined}
        slotProps={!onDownloadFullFile ? { 
          toolbar: {
            csvOptions: {
              allColumns: includeHiddenColumnInExport,
              disableToolbarButton: disableDownloadAsCSVOptions
            },
            printOptions: {
              disableToolbarButton: disableDownloadByPrintOptions
            },
          }
        } : undefined}
        disableColumnSelector={disableColumnSelector}
      />
    </Box>
  );
};

export default SearchResultGrid;
